import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type {
  DesktopLicenseDetail,
  DesktopLicenseDto,
  IssuedDesktopLicense,
} from '@hesabat/contracts';
import { api } from './api';
import Hint from './Hint';
import Loading from './Loading';

interface Props {
  onError: (caught: unknown) => void;
}

type Screen = { kind: 'list' } | { kind: 'create' } | { kind: 'detail'; id: string };

const EVENT_LABELS: Record<string, string> = {
  license_create: 'إصدار الترخيص',
  license_update: 'تعديل',
  license_suspend: 'إيقاف',
  license_reactivate: 'إعادة تفعيل',
  license_rotate_key: 'مفتاح جديد',
  device_activate: 'تفعيل جهاز',
  device_revoke: 'إلغاء جهاز',
};

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ar-EG') : '—';
}

/** آخر نبض بصيغة يفهمها المشغّل — البرنامج ينبض كل ساعة وهو مفتوح. */
function lastSeen(value: string | null): string {
  if (!value) return 'لم يُفعَّل بعد';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60_000);
  if (minutes < 90) return 'شغّال الآن';
  if (minutes < 48 * 60) return 'منذ ' + Math.round(minutes / 60) + ' ساعة';
  return 'منذ ' + Math.round(minutes / 1440) + ' يوم';
}

function isExpired(license: Pick<DesktopLicenseDto, 'validUntil'>): boolean {
  return Boolean(license.validUntil && new Date(license.validUntil).getTime() < Date.now());
}

function statusPill(license: DesktopLicenseDto) {
  if (license.status === 'suspended') return <span className="pill bad">موقوف</span>;
  if (isExpired(license)) return <span className="pill bad">انتهى الاشتراك</span>;
  return <span className="pill">نشط</span>;
}

/**
 * تراخيص نسخة سطح المكتب: مفتاح لكل عميل، وإيقاف يصل للبرنامج مع أول اتصال بالإنترنت.
 * الإيقاف يجعل الشركة على جهاز العميل للقراءة فقط — لا يمسح دفترًا ولا يحجبه.
 */
export default function DesktopLicenses({ onError }: Props) {
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });
  const [licenses, setLicenses] = useState<DesktopLicenseDto[] | null>(null);
  const [signingEnabled, setSigningEnabled] = useState<boolean | null>(null);
  const [issued, setIssued] = useState<IssuedDesktopLicense | null>(null);

  const load = useCallback(() => {
    api.desktopLicenses().then(setLicenses).catch(onError);
    api
      .desktopLicenseConfig()
      .then((config) => setSigningEnabled(config.signingEnabled))
      .catch(onError);
  }, [onError]);

  useEffect(load, [load]);

  const showIssued = (result: IssuedDesktopLicense) => {
    setIssued(result);
    setScreen({ kind: 'detail', id: result.license.id });
    load();
  };

  return (
    <>
      {signingEnabled === false && (
        <p className="notice notice-lock">
          مفتاح توقيع التراخيص غير مضبوط على الخادم، فلن يتفعّل أى جهاز حتى يُضبط.
        </p>
      )}

      {issued && <IssuedKey issued={issued} onClose={() => setIssued(null)} />}

      {screen.kind === 'list' && (
        <div className="panel">
          <div className="panel-head">
            <h2>نسخ سطح المكتب</h2>
            <button type="button" className="primary" onClick={() => setScreen({ kind: 'create' })}>
              ترخيص جديد
            </button>
          </div>
          <p className="sub">
            كل عميل اشترى نسخة سطح المكتب له مفتاح تفعيل. من هنا تعرف مين شغّال وعلى أى إصدار، وتوقف
            أى نسخة: الإيقاف يوصل للبرنامج أول ما الجهاز يتصل بالإنترنت.
          </p>
          {!licenses && <Loading />}
          {licenses && licenses.length === 0 && <p className="sub">لا توجد تراخيص بعد.</p>}
          {licenses && licenses.length > 0 && (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>العميل</th>
                    <th>الحالة</th>
                    <th>الأجهزة</th>
                    <th>آخر اتصال</th>
                    <th>الإصدار</th>
                    <th>نهاية الاشتراك</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <tr
                      key={license.id}
                      className="clickable"
                      tabIndex={0}
                      onClick={() => setScreen({ kind: 'detail', id: license.id })}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') setScreen({ kind: 'detail', id: license.id });
                      }}
                    >
                      <td>{license.customerName}</td>
                      <td>{statusPill(license)}</td>
                      <td className="num-cell">
                        {license.activeDevices} / {license.maxDevices}
                      </td>
                      <td>{lastSeen(license.lastSeenAt)}</td>
                      <td className="mono">{license.appVersion ?? '—'}</td>
                      <td>{license.validUntil ? formatDate(license.validUntil) : 'بلا نهاية'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {screen.kind === 'create' && (
        <CreateLicense
          onCancel={() => setScreen({ kind: 'list' })}
          onCreated={showIssued}
          onError={onError}
        />
      )}

      {screen.kind === 'detail' && (
        <LicenseDetail
          key={screen.id}
          id={screen.id}
          onBack={() => {
            setIssued(null);
            load();
            setScreen({ kind: 'list' });
          }}
          onRotated={showIssued}
          onError={onError}
        />
      )}
    </>
  );
}

function IssuedKey({ issued, onClose }: { issued: IssuedDesktopLicense; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="panel">
      <h2>مفتاح التفعيل لـ {issued.license.customerName}</h2>
      <p className="mono license-key" dir="ltr">
        {issued.licenseKey}
      </p>
      <p className="notice">
        المفتاح ده بيظهر <strong>مرة واحدة بس</strong> — انسخه وابعته للعميل دلوقتى. لو ضاع، اعمل
        مفتاح جديد من صفحة الترخيص (الأجهزة المفعّلة مش هتتأثر).
      </p>
      <div className="form-actions">
        <button
          type="button"
          className="primary"
          onClick={() => {
            void navigator.clipboard.writeText(issued.licenseKey).then(() => setCopied(true));
          }}
        >
          {copied ? 'اتنسخ ✓' : 'نسخ المفتاح'}
        </button>
        <button type="button" className="ghost" onClick={onClose}>
          تمام، نسخته
        </button>
      </div>
    </div>
  );
}

function CreateLicense({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: (issued: IssuedDesktopLicense) => void;
  onError: (caught: unknown) => void;
}) {
  const [customerName, setCustomerName] = useState('');
  const [maxDevices, setMaxDevices] = useState('1');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      onCreated(
        await api.createDesktopLicense({
          customerName: customerName.trim(),
          maxDevices: Number(maxDevices) || 1,
          validUntil: validUntil || null,
          notes: notes.trim() || null,
        }),
      );
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" noValidate onSubmit={(event) => void submit(event)}>
      <h2>ترخيص جديد</h2>
      <div className="form-row">
        <label className="grow">
          اسم العميل
          <input
            value={customerName}
            maxLength={200}
            required
            onChange={(event) => setCustomerName(event.target.value)}
          />
          <Hint>يظهر فى البرنامج عند العميل: «النسخة مفعّلة لـ …».</Hint>
        </label>
        <label>
          عدد الأجهزة
          <input
            type="number"
            min={1}
            value={maxDevices}
            onChange={(event) => setMaxDevices(event.target.value)}
          />
          <Hint>كام جهاز يتفعّل بنفس المفتاح.</Hint>
        </label>
        <label>
          آخر يوم فى الاشتراك
          <input
            type="date"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
          <Hint>فاضى = بلا نهاية. بعده البرنامج يبقى للقراءة فقط لحد ما تمدّه.</Hint>
        </label>
      </div>
      <label>
        ملاحظات
        <input value={notes} maxLength={1000} onChange={(event) => setNotes(event.target.value)} />
      </label>
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || !customerName.trim()}>
          إصدار المفتاح
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          إلغاء
        </button>
      </div>
    </form>
  );
}

function LicenseDetail({
  id,
  onBack,
  onRotated,
  onError,
}: {
  id: string;
  onBack: () => void;
  onRotated: (issued: IssuedDesktopLicense) => void;
  onError: (caught: unknown) => void;
}) {
  const [license, setLicense] = useState<DesktopLicenseDetail | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [maxDevices, setMaxDevices] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const show = useCallback((loaded: DesktopLicenseDetail) => {
    setLicense(loaded);
    setCustomerName(loaded.customerName);
    setMaxDevices(String(loaded.maxDevices));
    setValidUntil(loaded.validUntil ? loaded.validUntil.slice(0, 10) : '');
    setNotes(loaded.notes ?? '');
  }, []);

  useEffect(() => {
    api.desktopLicense(id).then(show).catch(onError);
  }, [id, show, onError]);

  async function run(action: () => Promise<DesktopLicenseDetail>): Promise<void> {
    setBusy(true);
    try {
      show(await action());
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (!license) return <Loading />;

  const activeDevices = license.devices.filter((device) => device.revokedAt === null);

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <h2>
            {license.customerName} {statusPill(license)}
          </h2>
          <button type="button" className="ghost" onClick={onBack}>
            كل التراخيص
          </button>
        </div>
        <div className="cards">
          <div className="card">
            <div className="label">الأجهزة المفعّلة</div>
            <div className="num">
              {license.activeDevices} / {license.maxDevices}
            </div>
          </div>
          <div className="card">
            <div className="label">آخر اتصال</div>
            <div className="num">{lastSeen(license.lastSeenAt)}</div>
          </div>
          <div className="card">
            <div className="label">نهاية الاشتراك</div>
            <div className="num">
              {license.validUntil ? formatDate(license.validUntil) : 'بلا نهاية'}
            </div>
          </div>
          <div className="card">
            <div className="label">المفتاح ينتهى بـ</div>
            <div className="num mono" dir="ltr">
              …{license.keyHint}
            </div>
          </div>
        </div>
        {license.status === 'suspended' && (
          <p className="notice">
            موقوف منذ {formatDate(license.suspendedAt)}: {license.suspendedReason}
          </p>
        )}

        <h3>الأجهزة</h3>
        {license.devices.length === 0 ? (
          <p className="sub">
            لم يُفعَّل أى جهاز بعد. العميل يكتب المفتاح فى أول خطوة من معالج الإعداد.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الجهاز</th>
                  <th>الشركة</th>
                  <th>الإصدار</th>
                  <th>آخر اتصال</th>
                  <th>فُعّل</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {license.devices.map((device) => (
                  <tr key={device.id} className={device.revokedAt ? 'muted' : undefined}>
                    <td>{device.deviceName}</td>
                    <td>{device.companyName ?? '—'}</td>
                    <td className="mono">{device.appVersion ?? '—'}</td>
                    <td>
                      {device.revokedAt
                        ? 'أُلغى ' + formatDate(device.revokedAt)
                        : lastSeen(device.lastSeenAt)}
                    </td>
                    <td>{formatDate(device.activatedAt)}</td>
                    <td>
                      {!device.revokedAt && (
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          onClick={() => {
                            if (
                              !window.confirm(
                                'إلغاء تفعيل ' +
                                  device.deviceName +
                                  '؟ البرنامج عليه يبقى للقراءة فقط لحد ما يتفعّل من جديد.',
                              )
                            )
                              return;
                            void run(() => api.revokeDesktopDevice(id, device.id));
                          }}
                        >
                          إلغاء الجهاز
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeDevices.length >= license.maxDevices && (
          <p className="sub">
            المفتاح مفعّل على أقصى عدد أجهزة. جهاز جديد يحتاج رفع العدد أو إلغاء جهاز قديم.
          </p>
        )}
      </div>

      <form
        className="panel"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void run(() =>
            api.updateDesktopLicense(id, {
              customerName: customerName.trim(),
              maxDevices: Number(maxDevices) || 1,
              validUntil: validUntil || null,
              notes: notes.trim() || null,
            }),
          );
        }}
      >
        <h2>بيانات الترخيص</h2>
        <div className="form-row">
          <label className="grow">
            اسم العميل
            <input
              value={customerName}
              maxLength={200}
              onChange={(event) => setCustomerName(event.target.value)}
            />
          </label>
          <label>
            عدد الأجهزة
            <input
              type="number"
              min={1}
              value={maxDevices}
              onChange={(event) => setMaxDevices(event.target.value)}
            />
          </label>
          <label>
            آخر يوم فى الاشتراك
            <input
              type="date"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
            />
            <Hint>التمديد يوصل للبرنامج مع أول اتصال بالإنترنت.</Hint>
          </label>
        </div>
        <label>
          ملاحظات
          <input
            value={notes}
            maxLength={1000}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        <div className="form-actions">
          <button type="submit" className="primary" disabled={busy || !customerName.trim()}>
            حفظ
          </button>
          <button
            type="button"
            className="ghost"
            disabled={busy}
            onClick={() => {
              if (
                !window.confirm(
                  'إصدار مفتاح جديد؟ القديم مش هيفعّل أجهزة جديدة، والأجهزة المفعّلة تفضل شغّالة.',
                )
              )
                return;
              setBusy(true);
              api
                .rotateDesktopLicenseKey(id)
                .then((result) => {
                  show(result.license);
                  onRotated(result);
                })
                .catch(onError)
                .finally(() => setBusy(false));
            }}
          >
            مفتاح جديد
          </button>
        </div>
      </form>

      <div className="panel">
        <h2>{license.status === 'suspended' ? 'إعادة التفعيل' : 'إيقاف النسخة'}</h2>
        {license.status === 'suspended' ? (
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void run(() => api.reactivateDesktopLicense(id))}
            >
              تفعيل النسخة
            </button>
            <Hint block>البرنامج يرجع يشتغل عادى مع أول اتصال بالإنترنت.</Hint>
          </div>
        ) : (
          <>
            <div className="form-row">
              <label className="grow">
                سبب الإيقاف (يظهر للعميل فى البرنامج)
                <input
                  value={reason}
                  maxLength={500}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
            </div>
            <p className="sub">
              الإيقاف يوصل لكل أجهزة العميل أول ما تتصل بالإنترنت (البرنامج بيسأل كل ساعة). بعدها
              البرنامج يمنع الإضافة والتعديل، والبيانات تفضل محفوظة ومتاحة للقراءة والتصدير — مفيش
              حاجة بتتمسح. جهاز مقطوع عن الإنترنت يقف لوحده بعد مدة العمل بلا إنترنت.
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="ghost"
                disabled={busy || !reason.trim()}
                onClick={() => {
                  if (!window.confirm('إيقاف نسخة ' + license.customerName + '؟')) return;
                  void run(() => api.suspendDesktopLicense(id, { reason: reason.trim() }));
                }}
              >
                إيقاف النسخة
              </button>
            </div>
          </>
        )}
      </div>

      {license.events.length > 0 && (
        <div className="panel">
          <h2>السجل</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>الحدث</th>
                  <th>بواسطة</th>
                </tr>
              </thead>
              <tbody>
                {license.events.map((event, index) => (
                  <tr key={index}>
                    <td>{new Date(event.createdAt).toLocaleString('ar-EG')}</td>
                    <td>{EVENT_LABELS[event.action] ?? event.action}</td>
                    <td className="mono">{event.actorEmail ?? 'البرنامج'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
